import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import _ from "lodash";
import styled, { css } from "styled-components";

import EditLabModal from "components/modals/edit-lab-modal";
import { Breadcrumb, Button, Container, Frame, RowWrapper, TExtra } from "components/ui-kit/styled-templates";

import LabsApi from "api/labs-api";

import ModalsService from "services/modals-service";

import useBodyBackground from "hooks/useBodyBackground";
import { useStorageListener } from "hooks/useStorage";

import { EModals } from "types/enums";

const LabsPage = () => {
    const { t } = useTranslation();

    useBodyBackground(`white`);

    const myUniversityId = useStorageListener(`myUniversityId`);
    const labs = useStorageListener((state) => _.values(state?.labs)?.filter?.((i) => i?.universityId === myUniversityId) ?? []);

    const handlers = {
        createLab: () => {
            ModalsService.showModal(EModals.CreateLab, { mode: `create` });
        },
        editLab: (id: string) => () => {
            ModalsService.showModal(EModals.CreateLab, { mode: `edit`, data: _.find(labs, { id }) });
        },
        deleteLab: (id: string) => async () => {
            ModalsService.showModality({
                title: `Удаление объекта научной инфраструктуры`,
                description: `Вы уверены, что хотите удалить объект научной инфраструктуры "${
                    _.find(labs, { id })?.details?.name ?? `без названия`
                }"?`,
                cancelButton: {
                    children: `Отмена`,
                    background: `gray`,
                    onClick: () => {},
                },
                confirmButton: {
                    children: `Удалить`,
                    background: `red`,
                    // onClick: async () => await LabsApi.deleteLab(id),
                    onClick: () => {
                        LabsApi.deleteLab(id);
                    },
                },
            });
        },
    };

    // @ts-ignore
    useEffect(LabsApi.getMyUniversityLabs, []);

    return (
        <>
            <EditLabModal  />

            <Container extra={`align-items: flex-start;`}>
                <Breadcrumb.Wrapper>
                    <Breadcrumb.Item to="/">{t("zif1xupavl")}</Breadcrumb.Item>
                </Breadcrumb.Wrapper>
                <RowWrapper extra={`h1 { margin: 0; };`}>
                    <h1>{t("vefihesjgi")}</h1>
                    <Button variant="outlined" onClick={handlers.createLab}>
                        {t("vgeqgzqwoph")}
                    </Button>
                </RowWrapper>
                <RowWrapper extra={`margin-top: 56px; flex-wrap: wrap; gap: 40px;`}>
                    {_.values(labs)?.map?.(({ details: item = {}, id, onModeration }: any, index: any) => (
                        <LabTab.Component key={index} {...{ item, id, handlers, onModeration }} />
                    ))}
                </RowWrapper>
                {!_.values(labs).length && <NotFoundWrapper>{t("59vpdsno59")}</NotFoundWrapper>}
            </Container>
        </>
    );
};

export enum ELabTabStatus {
    Accept = `accept`,
    Reject = `reject`,
    Process = `process`,
    None = `none`,
    Verification = `verification`,
}

export const LabTab = {
    Component: (props: { onModeration: boolean; item: any; id: string; handlers: { editLab: Function; deleteLab: Function } }) => {
        const { t } = useTranslation();

        const { item, id, handlers, onModeration } = props;

        const status = onModeration ? ELabTabStatus.Verification : ELabTabStatus.Accept;

        return (
            <LabTab.Wrapper>
                <LabTab.WarningSign status={status} />
                <LabTab.Name empty={_.isEmpty(item?.name)}>{item?.name ?? `Без имени`}</LabTab.Name>
                {/*<LabTab.Type>{EVENT_TYPES_OPTIONS?.find?.((i) => i?.value === item?.type)?.label}</LabTab.Type>*/}
                <LabTab.DetailsWrapper>
                    {/*<div>*/}
                    {/*    <div>Стоимость на 1 человека:</div>*/}
                    {/*    <strong>{item?.price} ₽</strong>*/}
                    {/*</div>*/}
                    {/*<div>*/}
                    {/*    <div>Даты проведения:</div>*/}
                    {/*    <strong>*/}
                    {/*        {moment(item?.dates?.from).format(`DD.MM`)} - {moment(item?.dates?.to).format(`DD.MM`)}*/}
                    {/*    </strong>*/}
                    {/*</div>*/}
                </LabTab.DetailsWrapper>
                {/*<Link to={`/labs/${id}`}>*/}
                {/*    <LabTab.Link color="blue">Предпросмотр</LabTab.Link>*/}
                {/*</Link>*/}
                <LabTab.Link color="blue" onClick={handlers.editLab(id)}>
                    {t("pzj6pcmxt3p")}
                </LabTab.Link>
                <LabTab.Link color="red" onClick={handlers.deleteLab(id)}>
                    {t("qlpwlvqsvva")}
                </LabTab.Link>
            </LabTab.Wrapper>
        );
    },
    Type: styled(Frame)`
        color: ${({ theme }) => theme.blue};
        font-size: 13px;
        line-height: 17px;
    `,
    WarningSign: styled(Frame)<{ status?: ELabTabStatus }>`
        width: 24px;
        height: 24px;
        background: url("${({ status = ELabTabStatus.None }) => require(`assets/icons/dormitory-status-${status}.svg`)}") no-repeat center center /
            contain;

        position: absolute;
        top: 10px;
        right: 10px;
    `,
    Wrapper: styled(Frame)<{ status?: ELabTabStatus }>`
        width: calc(50% - 20px);
        padding: 24px 20px;
        box-sizing: border-box;
        border: 1px solid ${({ theme }) => theme.halfblue};
        border-radius: 8px;
        align-items: flex-start;
        justify-content: flex-start;
        gap: 10px 0;
        position: relative;

        > * {
            margin: 0;
        }

        @media (max-width: 991px) {
            width: inherit;
        }
    `,
    Name: styled.h3<{ empty: boolean; extra?: TExtra }>`
        color: ${({ theme, empty }) => (empty ? theme.text.light : theme.text.primary)};

        ${({ extra }) => extra}
    `,
    Link: styled(Frame)<{ color: `red` | `blue`; direction?: `right` | `left` }>`
        color: ${({ theme, color }) => theme[color]};
        font-weight: 600;
        font-size: 16px;
        line-height: 20px;
        display: flex;
        align-items: center;
        flex-direction: row;
        cursor: pointer;

        ${({ direction = `right` }) => (direction === `right` ? `&:after` : `&:before`)} {
            content: "";
            width: 12px;
            height: 12px;
            background: url("${({ color }) => require(`assets/icons/arrow-right-${color}.svg`)}") no-repeat center center / contain;
            margin-left: 8px;
            transition: 0.2s;

            ${({ direction = `right` }) =>
                direction === `left` &&
                css`
                    transform: rotate(180deg);
                    margin-left: unset;
                    margin-right: 8px;
                `}
        }

        &:hover {
            ${({ direction = `right` }) => (direction === `right` ? `&:after` : `&:before`)} {
                transform: translateX(${({ direction = `right` }) => (direction === `right` ? 3 : -3)}px)
                    ${({ direction = `right` }) => (direction === `left` ? `rotate(180deg)` : ``)};
            }
        }
    `,
    DetailsWrapper: styled(RowWrapper)`
        > * {
            margin-bottom: 8px;
            width: 100%;

            p {
                margin-bottom: 8px;
            }
        }

        @media (max-width: 991px) {
            flex-wrap: wrap;
            > * {
                width: 50%;
            }
        } ;
    `,
};

export const NotFoundWrapper = styled(Frame)`
    width: inherit;
    color: ${({ theme }) => theme.grey};
    padding: 40px 0;
`;

export default LabsPage;
